/**
 * MIY Fresh storefront behaviour.
 *
 * Kept separate from theme.js so the Nest vendor file stays diffable against a
 * future theme update. Loaded deferred from layout/theme.liquid.
 *
 * Currently: product-card add-to-cart and quantity stepper behaviour.
 */
( function() {
	'use strict';

	/**
	 * Variant id -> quantity currently in the cart.
	 * Rebuilt from /cart.js rather than rendered into the markup, so it stays
	 * correct on a page served from cache and after any AJAX cart change.
	 */
	var cartQuantities = {};

	function fetchJSON( url, body ) {
		var options = body
			? {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
				body: JSON.stringify( body )
			}
			: { headers: { 'Accept': 'application/json' } };

		return fetch( url, options ).then( function( r ) {
			if ( ! r.ok ) {
				throw new Error( url + ' responded ' + r.status );
			}

			return r.json();
		} );
	}

	function routes() {
		var g = window.btyGlobals || {};

		return {
			add: g.cart_add_url || '/cart/add',
			change: g.cart_change_url || '/cart/change'
		};
	}

	// -- card helpers ------------------------------------------------------

	function selectedVariantId( card ) {
		var input = card.querySelector( '[data-miy-variant-input]' );

		return input ? input.value : null;
	}

	/**
	 * Show the stepper when this card's variant is in the cart, the ADD button
	 * when it is not. Re-run after every cart mutation so a card stays correct
	 * when the same product is changed from the cart page or another card.
	 */
	function renderCartState( card ) {
		var add = card.querySelector( '[data-miy-add]' );
		var stepper = card.querySelector( '[data-miy-stepper]' );
		var qtyEl = card.querySelector( '[data-miy-qty]' );
		if ( ! add || ! stepper ) {
			return;
		}

		var qty = cartQuantities[ selectedVariantId( card ) ] || 0;

		if ( qty > 0 ) {
			qtyEl.textContent = qty;
			stepper.hidden = false;
			add.hidden = true;
		} else {
			stepper.hidden = true;
			add.hidden = false;
		}
	}

	function renderAllCartStates() {
		document.querySelectorAll( '[data-miy-card]' ).forEach( renderCartState );
	}

	function setBusy( card, busy ) {
		card.classList.toggle( 'is-busy', !! busy );
	}

	/**
	 * Refresh the header cart count and mini-cart markup, reusing the theme's
	 * own helpers when they are present so both carts stay in agreement.
	 */
	function syncCartChrome( response ) {
		if ( typeof window.btyCartItemCount === 'function' && typeof response.item_count !== 'undefined' ) {
			window.btyCartItemCount( response.item_count );
		}

		var sections = response.sections;
		if ( ! sections || typeof window.btyGetSectionHtml !== 'function' ) {
			return;
		}

		var miniCart = document.querySelector( '.header-icons .mini-cart' );
		if ( miniCart && sections['mini-cart'] ) {
			miniCart.innerHTML = window.btyGetSectionHtml( sections['mini-cart'], '.mini-cart' );

			// Register any .lazy-image nodes the injected markup brought with it.
			if ( typeof window.btyAnimationImageLoad === 'function' ) {
				window.btyAnimationImageLoad( miniCart );
			}
		}
	}

	function refreshQuantities() {
		return fetchJSON( '/cart.js' ).then( function( cart ) {
			cartQuantities = {};
			( cart.items || [] ).forEach( function( item ) {
				cartQuantities[ item.variant_id ] = item.quantity;
			} );

			renderAllCartStates();

			return cart;
		} );
	}

	// -- actions -----------------------------------------------------------

	function addToCart( card ) {
		var id = selectedVariantId( card );
		if ( ! id ) {
			return;
		}

		setBusy( card, true );

		fetchJSON( routes().add, {
			items: [ { id: Number( id ), quantity: 1 } ],
			sections: [ 'mini-cart', 'cart-count' ]
		} )
			.then( function( res ) {
				syncCartChrome( res );

				// The add response does not carry line quantities, so re-read
				// the cart rather than guessing at the new total.
				return refreshQuantities();
			} )
			.catch( function( e ) {
				console.error( '[miy] add to cart failed', e );
			} )
			.finally( function() {
				setBusy( card, false );
			} );
	}

	function changeQuantity( card, delta ) {
		var id = selectedVariantId( card );
		var current = cartQuantities[ id ] || 0;
		var next = current + delta;
		if ( ! id || next < 0 ) {
			return;
		}

		setBusy( card, true );

		// /cart/change accepts a variant id for `id`. Quantity 0 removes the line.
		fetchJSON( routes().change, {
			id: String( id ),
			quantity: next,
			sections: [ 'mini-cart', 'cart-count' ],
			sections_url: window.location.pathname
		} )
			.then( function( res ) {
				syncCartChrome( res );

				cartQuantities = {};
				( res.items || [] ).forEach( function( item ) {
					cartQuantities[ item.variant_id ] = item.quantity;
				} );

				renderAllCartStates();
			} )
			.catch( function( e ) {
				console.error( '[miy] cart change failed', e );
			} )
			.finally( function() {
				setBusy( card, false );
			} );
	}

	/**
	 * Point the card's form at a different weight.
	 *
	 * The price is read from the option rather than refetched, so switching is
	 * instant, and the cart state is re-rendered because the newly selected
	 * weight may already be in the cart at a different quantity.
	 */
	function selectSize( card, select ) {
		var option = select.options[ select.selectedIndex ];
		if ( ! option ) {
			return;
		}

		var input = card.querySelector( '[data-miy-variant-input]' );
		if ( input ) {
			input.value = option.value;
		}

		var price = card.querySelector( '[data-miy-price]' );
		if ( price && option.dataset.price ) {
			price.innerHTML = option.dataset.price;
		}

		// Any compare-at price belonged to the previous weight; showing it
		// against a new price would be a false saving.
		var compare = card.querySelector( '[data-miy-compare]' );
		if ( compare ) {
			compare.remove();
		}

		renderCartState( card );
	}

	// -- wishlist ----------------------------------------------------------

	// The theme stores wishlist product ids as a JSON array under this key
	// (assets/theme.js:2415). Reading and writing the same key keeps the header
	// count and the wishlist page correct, without this card having to adopt the
	// vendor `.product-card` class and inherit all of its styling with it.
	var WISHLIST_KEY = 'wishlist-ids';

	function readWishlist() {
		try {
			var raw = localStorage.getItem( WISHLIST_KEY );
			var parsed = raw ? JSON.parse( raw ) : [];

			return Array.isArray( parsed ) ? parsed.map( String ) : [];
		} catch ( e ) {
			// Corrupt or unavailable storage (private mode) — treat as empty
			// rather than breaking every card on the page.
			return [];
		}
	}

	function writeWishlist( ids ) {
		try {
			localStorage.setItem( WISHLIST_KEY, JSON.stringify( ids ) );
		} catch ( e ) {
			return false;
		}

		if ( typeof window.btyUpdateCount === 'function' ) {
			window.btyUpdateCount( ids );
		}

		return true;
	}

	function renderWishlistState() {
		var ids = readWishlist();

		document.querySelectorAll( '[data-miy-wish]' ).forEach( function( btn ) {
			var on = ids.indexOf( String( btn.dataset.productId ) ) !== -1;
			btn.classList.toggle( 'is-active', on );
			btn.setAttribute( 'aria-pressed', on ? 'true' : 'false' );
		} );
	}

	function toggleWishlist( btn ) {
		var id = String( btn.dataset.productId );
		var ids = readWishlist();
		var at = ids.indexOf( id );

		if ( at === -1 ) {
			ids.push( id );
		} else {
			ids.splice( at, 1 );
		}

		if ( writeWishlist( ids ) ) {
			renderWishlistState();
		}
	}

	// -- wiring ------------------------------------------------------------

	// One delegated listener on the document, so cards injected later by AJAX
	// (filtering, pagination, quickview) work with no re-binding.
	function onClick( e ) {
		var card = e.target.closest ? e.target.closest( '[data-miy-card]' ) : null;
		if ( ! card ) {
			return;
		}

		var wish = e.target.closest( '[data-miy-wish]' );
		if ( wish ) {
			e.preventDefault();
			toggleWishlist( wish );
			return;
		}

		var step = e.target.closest( '[data-miy-step]' );
		if ( step ) {
			e.preventDefault();
			changeQuantity( card, Number( step.dataset.miyStep ) );
			return;
		}

		var add = e.target.closest( '[data-miy-add]' );
		if ( add && ! add.disabled ) {
			// Cancels the form's native submit. The card deliberately does not
			// carry theme.js's .add-to-cart-button hook, so nothing else is
			// listening on this button.
			e.preventDefault();
			addToCart( card );
		}
	}

	function init() {
		if ( ! document.querySelector( '[data-miy-card]' ) ) {
			return;
		}

		document.addEventListener( 'click', onClick );

		// Delegated for the same reason as the click handler: cards injected
		// later by filtering or pagination work with no re-binding.
		document.addEventListener( 'change', function( e ) {
			var select = e.target.closest ? e.target.closest( '[data-miy-size]' ) : null;
			if ( ! select ) {
				return;
			}

			var card = select.closest( '[data-miy-card]' );
			if ( card ) {
				selectSize( card, select );
			}
		} );

		renderWishlistState();

		refreshQuantities().catch( function( e ) {
			// A failed read just means every card keeps showing ADD, which is
			// still usable — do not let it break the click wiring.
			console.error( '[miy] could not read cart', e );
		} );
	}

	/* ----------------------------------------------------------------------
	   Mobile drawer: keyboard operability and focus handling.
	   ----------------------------------------------------------------------
	   `sections/header.liquid` renders the hamburger as a bare
	   `<span class="toggle-panel">` and theme.js binds `onclick` to it. A span
	   is not focusable and does not fire a click from Enter or Space, so the
	   ONLY navigation menu on a phone could not be opened by a keyboard or a
	   switch — WCAG 2.1.1 Keyboard, Level A. Nothing announced it either: no
	   role, no accessible name, no expanded state.

	   Enhanced from here rather than by editing the section, which is vendor.
	   We add the semantics the element should have had and let theme.js keep
	   its own click handler untouched — the keydown below dispatches a real
	   click rather than duplicating the open logic, so there is one code path
	   and no chance of the two drifting.

	   Focus is moved into the drawer on open and returned to the toggle on
	   close, because a drawer that opens behind the focus ring leaves a
	   keyboard user tabbing through the page underneath it. Escape closes,
	   which is what every other dismissible surface on the web does. */
	function enhanceDrawer() {
		var toggle = document.querySelector( '.toggle-panel' );
		var panel = document.querySelector( '.site-panel' );

		if ( ! toggle || ! panel || toggle.dataset.miyDrawerEnhanced ) {
			return;
		}
		toggle.dataset.miyDrawerEnhanced = '1';

		var OPEN_CLASS = 'site-panel-open';
		var root = document.documentElement;

		if ( ! panel.id ) {
			panel.id = 'miy-site-panel';
		}

		toggle.setAttribute( 'role', 'button' );
		toggle.setAttribute( 'tabindex', '0' );
		toggle.setAttribute( 'aria-controls', panel.id );
		toggle.setAttribute( 'aria-expanded', 'false' );

		// The span holds only an icon, so it has no accessible name of its own.
		if ( ! toggle.getAttribute( 'aria-label' ) ) {
			toggle.setAttribute( 'aria-label', toggle.dataset.miyLabel || 'Menu' );
		}

		toggle.addEventListener( 'keydown', function( e ) {
			if ( 'Enter' !== e.key && ' ' !== e.key && 'Spacebar' !== e.key ) {
				return;
			}
			// Space scrolls the page by default, and a span gets no free
			// activation behaviour to inherit.
			e.preventDefault();
			toggle.click();
		} );

		function close() {
			var closeButton = panel.querySelector( '.site-panel-close' );
			if ( closeButton ) {
				closeButton.click();
			} else {
				root.classList.remove( OPEN_CLASS );
			}
		}

		document.addEventListener( 'keydown', function( e ) {
			if ( 'Escape' === e.key && root.classList.contains( OPEN_CLASS ) ) {
				close();
			}
		} );

		// theme.js toggles the class on <html> and exposes no event, so the
		// class itself is the signal. An observer keeps aria-expanded honest
		// however the drawer was opened or closed — including by the vendor's
		// own outside-click handler, which we do not wrap.
		new MutationObserver( function() {
			var open = root.classList.contains( OPEN_CLASS );
			toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );

			if ( open ) {
				var target = panel.querySelector( '.site-panel-close' ) || panel;
				if ( panel === target && ! target.hasAttribute( 'tabindex' ) ) {
					target.setAttribute( 'tabindex', '-1' );
				}
				// RETRIED ACROSS FRAMES, not fired once. The class lands when
				// the drawer STARTS opening, and the panel animates in — for as
				// long as it is still `visibility: hidden` or unrendered,
				// `focus()` is a SILENT no-op. A single rAF was measured landing
				// inside that window: the ring stayed on the hamburger and a
				// keyboard user was still outside the drawer. A fixed timeout
				// would only move the guess, so this watches for the element to
				// become focusable instead, and stops the moment it does.
				//
				// A tap also focuses the toggle itself on mousedown, after this
				// observer would otherwise have run, so retrying settles that
				// ordering too.
				var frames = 0;
				( function attempt() {
					if ( ! root.classList.contains( OPEN_CLASS ) ) {
						return;
					}
					if ( panel.contains( document.activeElement ) ) {
						return;
					}
					target.focus();
					if ( panel.contains( document.activeElement ) ) {
						return;
					}
					// ~30 frames is half a second at 60fps, comfortably past any
					// drawer transition, and bounded so a panel that never
					// becomes focusable cannot spin forever.
					if ( ++frames < 30 ) {
						requestAnimationFrame( attempt );
					}
				}() );
			} else if ( panel.contains( document.activeElement ) ) {
				// Only reclaim focus if it is still inside the drawer we just
				// closed; if the shopper has tabbed elsewhere, leave them be.
				toggle.focus();
			}
		} ).observe( root, { attributes: true, attributeFilter: [ 'class' ] } );
	}

	/* ----------------------------------------------------------------------
	   Drawer navigation: sub-menu disclosure.
	   ----------------------------------------------------------------------
	   `snippets/miy-mobile-nav.liquid` renders each parent as a link plus a
	   separate `<button aria-expanded>` controlling the sub-list, so the parent
	   category is still reachable in one tap and the disclosure is a real
	   button rather than a clickable div.

	   Delegated from the panel rather than bound per button: the drawer is
	   re-rendered whole in the theme editor, and a delegated listener survives
	   that without re-binding.

	   `hidden` and `aria-expanded` are flipped together and read back from the
	   DOM rather than tracked in a variable, so the two cannot drift apart —
	   the attribute IS the state. */
	function enhanceDrawerNav() {
		var nav = document.querySelector( '.miy-mnav' );

		if ( ! nav || nav.dataset.miyNavEnhanced ) {
			return;
		}
		nav.dataset.miyNavEnhanced = '1';

		nav.addEventListener( 'click', function( e ) {
			var button = e.target.closest( '.miy-mnav__expand' );
			if ( ! button ) {
				return;
			}

			var sub = document.getElementById( button.getAttribute( 'aria-controls' ) );
			if ( ! sub ) {
				return;
			}

			var open = 'true' === button.getAttribute( 'aria-expanded' );
			button.setAttribute( 'aria-expanded', open ? 'false' : 'true' );
			sub.hidden = open;
		} );
	}

	/* ----------------------------------------------------------------------
	   The once-per-visit offer dialog  (sections/miy-offer-dialog.liquid)

	   A SESSION COOKIE, not sessionStorage. Both survive a page navigation,
	   but sessionStorage is scoped per TAB: a shopper who opens a product in a
	   second tab would be shown the dialog again, which is not what "once per
	   visit" means to them. A cookie with no `expires` is shared across tabs
	   and dies when the browser closes, which is exactly the visit boundary.

	   Everything that closes the dialog — the cross, the dismiss link, Escape,
	   a click on the backdrop — funnels through `dialog.close()`, and the
	   cookie is written in ONE place, the `close` event. That is the whole
	   reason to use a native <dialog>: Escape is handled by the browser and
	   still reaches our bookkeeping, so there is no path that closes the
	   dialog without recording it.
	   ---------------------------------------------------------------------- */
	var OFFER_COOKIE = 'miy_offer_seen';

	function offerSeen() {
		try {
			// Anchored, so a future cookie whose name merely ENDS with ours
			// (say `shop_miy_offer_seen`) cannot suppress the dialog.
			return new RegExp( '(^|;\\s*)' + OFFER_COOKIE + '=1' ).test( document.cookie );
		} catch ( e ) {
			return false;
		}
	}

	function markOfferSeen() {
		try {
			// No `expires` and no `max-age` — that is what makes it a session
			// cookie. SameSite=Lax so it survives a normal click-through back
			// to the store without being sent on cross-site requests.
			document.cookie = OFFER_COOKIE + '=1; path=/; SameSite=Lax';
		} catch ( e ) {
			/* Cookies blocked. The dialog then reappears on the next page,
			   which is the vendor popup's behaviour anyway and affects only a
			   shopper who has switched cookies off entirely. Failing loudly
			   here would cost more than it saves. */
		}
	}

	function initOfferDialog() {
		var dialog = document.querySelector( '[data-miy-offer]' );

		if ( ! dialog || dialog.dataset.miyOfferBound ) {
			return;
		}

		// `showModal` is what buys the focus trap, the inert background and
		// Escape. Without it this would be a div that merely looks like a
		// dialog, so there is no fallback worth shipping: leave it closed.
		if ( 'function' !== typeof dialog.showModal ) {
			return;
		}

		dialog.dataset.miyOfferBound = '1';

		// The theme editor re-renders a section on every settings change. A
		// merchant editing the copy needs to SEE it, so the cookie gate is
		// skipped there — it would otherwise open once and never again for the
		// rest of their editing session.
		var inEditor = !! ( window.Shopify && window.Shopify.designMode );

		if ( offerSeen() && ! inEditor ) {
			return;
		}

		// The backdrop is not a child element: a click on it lands on the
		// <dialog> itself, while a click on the content lands on .miy-offer__inner.
		dialog.addEventListener( 'click', function( e ) {
			if ( e.target === dialog ) {
				dialog.close();
			}
		} );

		dialog.addEventListener( 'click', function( e ) {
			if ( e.target.closest( '[data-miy-offer-dismiss]' ) ) {
				dialog.close();
			}
		} );

		var delay = parseInt( dialog.dataset.delay, 10 );
		if ( isNaN( delay ) || delay < 0 ) {
			delay = 3;
		}

		/* The picture is `loading="lazy"`, and a lazy image inside a closed
		   dialog has never been in the viewport, so nothing has fetched it.
		   Opening the dialog starts the request THEN — which showed as a
		   cream panel for a beat before the photograph appeared.

		   So the fetch is started when the timer fires and the dialog waits
		   for it, rather than opening around a hole. This costs nothing at
		   load: the request still begins after the delay, long past the point
		   where it could compete with the hero for LCP, which is the whole
		   reason the image is lazy in the first place.

		   Capped, because a dialog that never opens is worse than one that
		   opens without its picture: a slow or broken image releases it after
		   1.5s regardless. */
		function reveal() {
			// The merchant may have closed it by hand in the editor, and a second
			// showModal() on an open dialog throws.
			if ( dialog.open ) {
				return;
			}

			dialog.showModal();

			/* RECORDED AT SHOW TIME, NOT ON CLOSE — the correctness argument for
			   this whole feature.

			   Recording when the dialog closes only covers a shopper who closes
			   it. One who reads the offer and then clicks a category, the logo, or
			   any other link never fires a `close` event, so the dialog opened
			   again on the next page and the next — the exact nagging that "once
			   per visit" exists to prevent. Special-casing the call to action
			   patched one route out of many and left the rest.

			   "Opened once this visit" is true the instant it opens, so that is
			   when it is written. No exit path can miss it. */
			markOfferSeen();
		}

		/* The picture is `loading="lazy"`, and a lazy image inside a closed
		   dialog has never been in the viewport, so nothing has fetched it.
		   Opening the dialog starts the request THEN, which showed as a cream
		   panel for a beat before the photograph arrived.

		   So the fetch starts when the timer fires and the dialog waits for it
		   rather than opening around a hole. This costs nothing at load: the
		   request still begins after the delay, long past the point where it
		   could compete with the hero for LCP — which is the entire reason the
		   image is lazy to begin with.

		   Capped, because a dialog that never opens is worse than one that opens
		   without its picture: a slow or broken image releases it after 1.5s. */
		function openWhenPictureReady() {
			var img = dialog.querySelector( 'img' );
			var released = false;

			function release() {
				if ( released ) {
					return;
				}
				released = true;
				reveal();
			}

			if ( ! img || img.complete ) {
				release();
				return;
			}

			img.loading = 'eager';
			img.addEventListener( 'load', release, { once: true } );
			img.addEventListener( 'error', release, { once: true } );
			window.setTimeout( release, 1500 );
		}

		window.setTimeout( openWhenPictureReady, delay * 1000 );
	}

	if ( 'loading' === document.readyState ) {
		document.addEventListener( 'DOMContentLoaded', enhanceDrawer );
		document.addEventListener( 'DOMContentLoaded', enhanceDrawerNav );
		document.addEventListener( 'DOMContentLoaded', initOfferDialog );
	} else {
		enhanceDrawer();
		enhanceDrawerNav();
		initOfferDialog();
	}

	document.addEventListener( 'shopify:section:load', function() {
		enhanceDrawer();
		enhanceDrawerNav();
		initOfferDialog();
	} );

	if ( 'loading' === document.readyState ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}

	// Theme editor: re-sync when a section is re-rendered.
	document.addEventListener( 'shopify:section:load', function() {
		refreshQuantities().catch( function() {} );
	} );
}() );
